import {Component, ViewChild} from '@angular/core';
import {isNotFound, NOT_FOUND, NotFound} from '@angular/core/primitives/di';
import {ActivatedRoute, Event, NavigationEnd, RouterLink, Scroll, UrlSegment} from '@angular/router';
import {MegaMenuItem, MenuItem} from 'primeng/api';
import {Menu} from 'primeng/menu';
import {CpmCUserApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-global/cpmc-user.api-model';
import {IBaseComponent} from '../../../../sdk/src/components/ibase/ibase.component';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {environment} from '../../../environments/environment';
import {LocationUtils} from '../../utils/location.util';

/**
 * Header component
 */
@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrl: './header.component.scss',
    imports: [PrimeNgImportsModule, RouterLink]
})
export class HeaderComponent extends IBaseComponent {
    private previousUrl?: string;

    // ===============================================================
    // ===============================================================

    @ViewChild('userMenu')
    protected userMenu?: Menu;

    protected menuItems!: MegaMenuItem[];
    protected userMenuItems!: MegaMenuItem[];
    protected breadcrumbs: MenuItem[] = [{icon: 'pi pi-home', routerLink: `/`, label: '...'}];
    protected currentUser?: CpmCUserApiModel;

    protected logoPath = 'images/logo/orange_logo_dev.png';

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    protected override async onReady(): Promise<void> {
        this.manageLogoEnvironment();
        await this.globalsService.sessionService.getIsReadyPromise();
        this.currentUser = this.globalsService.sessionService.getUser();
        this.initUserMenuItems();

        await this.globalsService.sessionService.getRouteContextIsReadyPromise();

        this.globalsService.router.events.subscribe((eventData) => {
            this.initSetup(eventData);

            let eventNav = eventData;
            if (eventData instanceof Scroll) {
                eventNav = eventData.routerEvent;
            }
            if (eventNav instanceof NavigationEnd) {
                this.updateLogicalMenuActiveState(eventNav.urlAfterRedirects);
                if (this.previousUrl === undefined || eventNav.url.split('?')[0] !== this.previousUrl) {
                    this.previousUrl = eventNav.url.split('?')[0];
                }
            }
        });

        this.initSetup(undefined, true);
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * Refresh the breadcrumb
     */
    public refreshBreadcrumbs() {
        this.initSetup(undefined, true);
    }

    // ===============================================================
    // ===============================================================

    /**
     * Update the breadcrumbs according to the current route
     * @param eventData The event of the router
     * @param force Force the update of the breadcrumbs if true
     */
    private async initSetup(eventData?: Event, force = false) {
        if (!(eventData instanceof NavigationEnd) && !force) {
            return;
        }

        await this.globalsService.sessionService.getRouteContextIsReadyPromise();

        this.breadcrumbs = [{label: 'Home', routerLink: `/`}];
        let routerLink = `/`;

        const replaceMap: Record<string, {label: string; replacePrevious: boolean; icon?: string}> = {};
        const customer = this.globalsService.sessionService.getCustomer();
        const organization = this.globalsService.sessionService.getOrganization();
        const location = this.globalsService.sessionService.getLocation();
        const project = this.globalsService.sessionService.getProject();
        const serviceRequest = this.globalsService.sessionService.getServiceRequest();

        // Static path segment overrides
        if (customer) {
            replaceMap[customer.uuid!] = {label: customer.name!, replacePrevious: true};
        }
        if (organization) {
            replaceMap[organization.uuid!] = {label: organization.name!, replacePrevious: false};
        }
        if (location) {
            replaceMap[location.uuid!] = {
                label: location.name!,
                replacePrevious: false,
                icon: LocationUtils.getIconForType(location.type)
            };
        }

        if (project) {
            replaceMap[project.uuid!] = {label: project.name!, replacePrevious: false, icon: 'pi pi-briefcase'};
        }

        if (serviceRequest)
            replaceMap[serviceRequest.uuid!] = {
                label: serviceRequest.id!,
                replacePrevious: false,
                icon: 'pi pi-hashtag'
            };

        let currentRoute: ActivatedRoute | null = this.globalsService.router.routerState.root;

        do {
            const childRoutes = currentRoute.children;
            currentRoute = null;

            childRoutes.forEach((route) => {
                if (route.outlet !== 'primary') {
                    return;
                }
                const routeSnapshot = route.snapshot;
                if (routeSnapshot?.url.length > 0) {
                    routeSnapshot.url.map((segment) => {
                        let label = '';

                        routerLink += '/' + segment.path;
                        routerLink = routerLink.replaceAll('//', '/');

                        if (routeSnapshot.title) {
                            label = routeSnapshot.title;
                        } else if (segment.path in replaceMap) {
                            const replacement = replaceMap[segment.path];
                            if (replacement.replacePrevious) {
                                const lastBreadcrumb = this.breadcrumbs[this.breadcrumbs.length - 1];
                                lastBreadcrumb.routerLink = routerLink;
                                lastBreadcrumb.label = replaceMap[segment.path].label;
                                return;
                            }

                            label = replacement.label;
                        } else {
                            label = this.getBreadcrumbLabelFromUrlSegment(segment);
                        }

                        this.breadcrumbs.push({
                            routerLink: routerLink,
                            label: label
                        });
                    });
                } else if (routeSnapshot.title) {
                    this.breadcrumbs.push({
                        routerLink: routerLink,
                        label: routeSnapshot.title
                    });
                }
                currentRoute = route;
            });
        } while (currentRoute);

        /*if (this.breadcrumbs.length === 1) {
            this.breadcrumbs[0].label = 'Customers';
        }*/
    }

    // ===============================================================
    // ===============================================================

    /**
     * Replace the menu items
     * @param menuItems New menu items
     */
    public setMenuItems(menuItems: MegaMenuItem[]) {
        this.menuItems = menuItems;
        this.updateLogicalMenuActiveState(this.globalsService.router.url);
    }

    /** Apply standard active styling to non-navigating menu groups based on their logical route. */
    private updateLogicalMenuActiveState(url: string): void {
        const currentPath = url.split(/[?#]/)[0].replace(/\/$/, '');

        this.menuItems = this.menuItems.map((item) => {
            const logicalPath = item['appLogicalRouterLink']?.replace(/\/$/, '');
            if (!logicalPath) return item;

            const styleClasses = new Set(item.styleClass?.split(/\s+/).filter(Boolean) ?? []);
            const isActive = currentPath === logicalPath || currentPath.startsWith(`${logicalPath}/`);

            if (isActive) styleClasses.add('app-logical-route-active');
            else styleClasses.delete('app-logical-route-active');

            return {...item, styleClass: Array.from(styleClasses).join(' ') || undefined};
        });
    }
    /**
     * Init user menu items
     */
    private initUserMenuItems() {
        this.userMenuItems = [
            {
                label: 'Logout',
                icon: 'pi pi-sign-out',
                command: () => {
                    this.globalsService.keycloakService.redirectToLogout();
                }
            }
        ];
    }

    // ===============================================================
    // ===============================================================

    /**
     * Try find an entry with the same router link in the mega menu items
     * @param routerLink Lookup router link
     * @returns Found entry or undefined
     */
    private tryFindEntryFromMegaMenuItems(routerLink: string): MegaMenuItem | MenuItem | undefined {
        if (!this.menuItems) return;

        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < this.menuItems.length; ++i) {
            const currentMegaMenuItem = this.menuItems[i];
            if (currentMegaMenuItem.routerLink == routerLink) return currentMegaMenuItem;
            if (
                currentMegaMenuItem['appLogicalRouterLink'] &&
                currentMegaMenuItem['appLogicalRouterLink'] == routerLink
            )
                return currentMegaMenuItem;

            if (!currentMegaMenuItem.items) continue;

            // eslint-disable-next-line @typescript-eslint/prefer-for-of
            for (let j = 0; j < currentMegaMenuItem.items.length; ++j) {
                const subResult = this.tryFindIconFromMenuItems(routerLink, currentMegaMenuItem.items[j]);
                if (!isNotFound(subResult)) return subResult;
            }
        }

        return undefined;
    }
    /**
     * Try find an entry with the same router link in  menu items
     * @param routerLink Lookup router link
     * @param menuItems In menu items
     * @returns Found entry or undefined
     */
    private tryFindIconFromMenuItems(routerLink: string, menuItems?: MenuItem[]): MenuItem | NotFound {
        if (!menuItems) return NOT_FOUND;

        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < menuItems.length; ++i) {
            const currentMenuItem = menuItems[i];

            if (currentMenuItem.routerLink == routerLink) return currentMenuItem;
            if (currentMenuItem['appLogicalRouterLink'] && currentMenuItem['appLogicalRouterLink'] == routerLink)
                return currentMenuItem;

            if (!currentMenuItem.items) continue;

            const subResult = this.tryFindIconFromMenuItems(routerLink, currentMenuItem.items);
            if (!isNotFound(subResult)) return subResult;
        }

        return NOT_FOUND;
    }
    /**
     * Generate a breadcrumb label from the url segment
     * @param segment Url segment
     * @returns Generated label
     */
    private getBreadcrumbLabelFromUrlSegment(segment: UrlSegment) {
        let label = segment.path.charAt(0).toUpperCase() + segment.path.slice(1);
        label = label.replace(/-/g, ' ');

        const labelWords = label.split(' ');
        const labelCapitalWords = labelWords.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());

        return labelCapitalWords.join(' ');
    }

    /**
     * Manage the logo based on the branch type
     */
    protected manageLogoEnvironment() {
        switch (environment.appEnvironment) {
            case 'pre-production':
                this.logoPath = 'images/logo/orange_logo_pre_prod.png';
                break;
            case 'production':
                this.logoPath = 'images/logo/orange_logo.svg';
                break;
            default:
                this.logoPath = 'images/logo/orange_logo_dev.png';
                break;
        }
    }
}
